import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { NotebookText } from 'lucide-react';
import { toast } from 'sonner';

import { Skeleton } from '@nesso/ui/primitives/skeleton';

import {
    type TelehealthSettingsValue,
    telehealthSettingsOptions,
    useUpdateTelehealthSettingsOptions,
} from '@/entities/practice-member-settings';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';
import { SettingsRow, SettingsRowActionButton } from '@/shared/ui/settings/settings-row';

import { getTelehealthLinkRowSummary } from '../../model/get-telehealth-link-row-summary';
import { TelehealthLinkForm } from './telehealth-link-row/telehealth-link-form';

const ROW_LABEL = 'Telehealth link';

const CLEARED_SETTINGS: TelehealthSettingsValue = {
    linkType: null,
    link: null,
    instructions: null,
};

export function TelehealthLinkRow() {
    const [isEditing, setIsEditing] = useState(false);
    const { data: settings, isError, isSuccess } = useQuery(telehealthSettingsOptions());
    const mutation = useMutation(useUpdateTelehealthSettingsOptions());
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);

    // A failed read must never be indistinguishable from "never configured" — that empty state's
    // own action would save over whatever is actually stored.
    useEffect(() => {
        if (isError) {
            toast.error('Unable to load telehealth settings. Try again.');
        }
    }, [isError]);

    const handleSave = (value: TelehealthSettingsValue) => {
        mutation.mutate(value, {
            onSuccess: () => setIsEditing(false),
            onError: () => toast.error('Unable to update telehealth link. Try again.'),
        });
    };

    // There is no delete endpoint any more — a settings row is never deleted. Clearing the link
    // is a write of the "not configured" value like any other.
    const handleDelete = () => {
        mutation.mutate(CLEARED_SETTINGS, {
            onError: () => toast.error('Unable to delete telehealth information. Try again.'),
        });
    };

    // Per-appointment links carry no stored URL or instructions, so the design's own annotation
    // waives the confirmation — there is nothing destructive to warn about.
    const handleDeleteClick = () => {
        if (settings?.value.linkType === 'PER_APPOINTMENT') {
            handleDelete();
            return;
        }

        openConfirmDialog({
            title: 'Delete telehealth information?',
            // Restores with the instructions field: 'Link and instructions will no longer be automatically added to the session.'
            description: 'Link will no longer be automatically added to the session.',
            confirmText: 'Delete',
            variant: 'destructive',
            onConfirm: handleDelete,
        });
    };

    // Not yet loaded — still fetching, or the fetch failed — so stay in this shape rather than
    // rendering the empty ("None provided") state that a save from here would overwrite.
    if (!isSuccess) {
        return <SettingsRow label={ROW_LABEL} caption={<Skeleton className="h-5 w-56" />} />;
    }

    if (isEditing) {
        return (
            <SettingsRow
                label={ROW_LABEL}
                action={{ label: 'Cancel', onClick: () => setIsEditing(false) }}
            >
                <TelehealthLinkForm
                    defaultSettings={settings.value}
                    isSubmitting={mutation.isPending}
                    onSubmit={handleSave}
                />
            </SettingsRow>
        );
    }

    const { caption, instructions, actionLabel } = getTelehealthLinkRowSummary(settings.value);

    const captionNode = (
        <div className={settings.value.linkType === 'SAME_FOR_ALL' ? 'truncate' : undefined}>
            {caption}
        </div>
    );

    if (actionLabel === 'Add') {
        return (
            <SettingsRow
                label={ROW_LABEL}
                caption={captionNode}
                action={{
                    label: actionLabel,
                    onClick: () => setIsEditing(true),
                    ariaLabel: `${actionLabel} telehealth link`,
                }}
            />
        );
    }

    return (
        <SettingsRow
            label={ROW_LABEL}
            caption={captionNode}
            trailing={
                <div className="flex items-center">
                    <SettingsRowActionButton
                        onClick={() => setIsEditing(true)}
                        aria-label={`${actionLabel} telehealth link`}
                        disabled={mutation.isPending}
                    >
                        {actionLabel}
                    </SettingsRowActionButton>
                    <SettingsRowActionButton
                        onClick={handleDeleteClick}
                        aria-label="Delete telehealth link"
                        disabled={mutation.isPending}
                    >
                        Delete
                    </SettingsRowActionButton>
                </div>
            }
        >
            {instructions && (
                <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-muted-foreground">
                    <NotebookText className="size-4 shrink-0" />
                    {/* Single line with a truncating child, not a wrapping one — a
                        multi-thousand character paste must shorten to an ellipsis rather
                        than grow the row. */}
                    <span className="min-w-0 flex-1 truncate">{instructions}</span>
                </div>
            )}
        </SettingsRow>
    );
}
